import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Printer, RefreshCw, Search, SlidersHorizontal, X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import {
  FETCH_CHUNK,
  REPORT_TABLE_HEADER_BG,
  REPORT_TABLE_HEADER_TEXT,
  REPORT_TABLE_HEADER_BORDER,
  applyExcelHeaderRowStyle,
  printHtmlInSameTab,
} from './reportPrintHelpers';

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function defaultFilters() {
  return {
    dateFrom: todayInput(),
    dateTo: todayInput(),
    keyword: '',
    employeeId: '',
    shipmentNosStr: '',
  };
}

/** Legacy splits ref list on "." (e.g. SHP1.SHP2). */
function shipmentNosFromStr(str) {
  if (!str || typeof str !== 'string') return [];
  return str
    .split('.')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Server filters shipment `Date`; keyword contains shipment or delivery-note ref; optional driver and ref list. */
function buildPayload(skip, take, f) {
  const d0 = new Date(f.dateFrom + 'T12:00:00');
  const d1 = new Date(f.dateTo + 'T12:00:00');
  const empRaw = f.employeeId === '' || f.employeeId == null ? 0 : Number(f.employeeId);
  const employeeId = Number.isNaN(empRaw) ? 0 : empRaw;
  const shipmentNosStr = (f.shipmentNosStr || '').trim();
  const shipmentNos = shipmentNosFromStr(shipmentNosStr);
  return {
    Skip: skip,
    Take: take,
    FromYear: d0.getFullYear(),
    FromMonth: d0.getMonth() + 1,
    FromDay: d0.getDate(),
    ToYear: d1.getFullYear(),
    ToMonth: d1.getMonth() + 1,
    ToDay: d1.getDate(),
    Keyword: (f.keyword || '').trim(),
    EmployeeId: employeeId,
    ShipmentNosStr: shipmentNosStr,
    ShipmentNos: shipmentNos,
    ClientId: 0,
    ClientIds: [],
    Status: '',
    IsPickupDate: false,
    IsCreatedDate: false,
    VoucherNo: '',
    BillNo: '',
    StatusIds: [],
    AreaIds: [],
    EmployeeIds: [],
    AgentIds: [],
    CustomerIds: [],
    ExpenseIds: [],
    IsPaid: false,
    IsNotPaid: false,
    IsByDelPickup: false,
  };
}

function formatDate(d) {
  if (d == null || d === '') return '—';
  if (typeof d === 'string') {
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
    const dt = new Date(d.Year, d.Month - 1, d.Day);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function num(n) {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  return Number.isNaN(v) ? '—' : v.toFixed(2);
}

function parseNumberField(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function boolYesNo(v) {
  if (v === true || v === 'true' || v === 1 || v === '1') return 'Yes';
  if (v === false || v === 'false' || v === 0 || v === '0') return 'No';
  return '—';
}

function dlvPercentDisplay(r) {
  const fromApi = r.DLVPer ?? r.dlvPer;
  if (fromApi != null && fromApi !== '') {
    const v = typeof fromApi === 'number' ? fromApi : parseFloat(fromApi);
    if (!Number.isNaN(v)) return `${v.toFixed(2)}%`;
  }
  const dlv = parseNumberField(r.TotalDLV ?? r.totalDLV);
  const rtn = parseNumberField(r.TotalRTN ?? r.totalRTN);
  const cnl = parseNumberField(r.TotalCNL ?? r.totalCNL);
  const den = dlv + rtn + cnl;
  if (den <= 0) return '—';
  return `${((dlv / den) * 100).toFixed(2)}%`;
}

function rowToFlat(r, i) {
  const dn = r.DeliveryNote ?? r.deliveryNote;
  const cb = r.CreatedBy ?? r.createdBy;
  return {
    '#': i + 1,
    RefNo: r.RefNo ?? r.refNo ?? '',
    Date: formatDate(r.Date ?? r.date),
    DeliveryNoteRef: dn?.RefNo ?? dn?.refNo ?? '',
    Driver: dn?.DriverName ?? dn?.driverName ?? '',
    CreatedBy: cb?.UserName ?? cb?.userName ?? '',
    ExpectedAmount: r.ExpectedAmount ?? r.expectedAmount ?? '',
    TotalExpense: r.TotalExpense ?? r.totalExpense ?? '',
    CashInHand: r.CashInHand ?? r.cashInHand ?? '',
    TotalExpected: r.TotalExpected ?? r.totalExpected ?? '',
    TotalCashInHand: r.TotalCashInHand ?? r.totalCashInHand ?? '',
    TotalIncome: r.TotalIncome ?? r.totalIncome ?? '',
    TotalVat: r.TotalVat ?? r.totalVat ?? '',
    TotalAdvance: r.TotalAdvance ?? r.totalAdvance ?? '',
    BalanceIncome: r.BalanceIncome ?? r.balanceIncome ?? '',
    TotalMoneyFromClient: r.TotalMoneyFromClient ?? r.totalMoneyFromClient ?? '',
    TotalClientMoney: r.TotalClientMoney ?? r.totalClientMoney ?? '',
    TotalDLV: r.TotalDLV ?? r.totalDLV ?? '',
    TotalRTN: r.TotalRTN ?? r.totalRTN ?? '',
    TotalCNL: r.TotalCNL ?? r.totalCNL ?? '',
    DLVPer: dlvPercentDisplay(r),
    IsApproved: boolYesNo(r.IsApproved ?? r.isApproved),
  };
}

function rowMatchesTableSearch(r, q) {
  if (!q) return true;
  const o = rowToFlat(r, 0);
  const { '#': _n, ...rest } = o;
  const hay = Object.values(rest)
    .map((x) => String(x ?? '').toLowerCase())
    .join(' ');
  return hay.includes(q);
}

const PRINT_COLUMN_DEFS = [
  { key: '#', label: '#' },
  { key: 'RefNo', label: 'Ref no' },
  { key: 'Date', label: 'Date' },
  { key: 'DeliveryNoteRef', label: 'Delivery note' },
  { key: 'Driver', label: 'Driver' },
  { key: 'CreatedBy', label: 'Created by' },
  { key: 'ExpectedAmount', label: 'Expected' },
  { key: 'TotalExpense', label: 'Expense' },
  { key: 'CashInHand', label: 'Cash in hand' },
  { key: 'TotalExpected', label: 'Total expected' },
  { key: 'TotalCashInHand', label: 'Total cash in hand' },
  { key: 'TotalIncome', label: 'Total income' },
  { key: 'TotalVat', label: 'Total VAT' },
  { key: 'TotalAdvance', label: 'Total advance' },
  { key: 'BalanceIncome', label: 'Balance income' },
  { key: 'TotalMoneyFromClient', label: 'Money from client' },
  { key: 'TotalClientMoney', label: 'Client money' },
  { key: 'TotalDLV', label: 'Delivered' },
  { key: 'TotalRTN', label: 'Returned' },
  { key: 'TotalCNL', label: 'Cancelled' },
  { key: 'DLVPer', label: 'Delivery %' },
  { key: 'IsApproved', label: 'Approved' },
];

const COL_COUNT = PRINT_COLUMN_DEFS.length;

const RIGHT_ALIGN_KEYS = new Set([
  'ExpectedAmount',
  'TotalExpense',
  'CashInHand',
  'TotalExpected',
  'TotalCashInHand',
  'TotalIncome',
  'TotalVat',
  'TotalAdvance',
  'BalanceIncome',
  'TotalMoneyFromClient',
  'TotalClientMoney',
  'TotalDLV',
  'TotalRTN',
  'TotalCNL',
]);

const SUMMARY_KEYS = [
  'ExpectedAmount',
  'TotalExpense',
  'CashInHand',
  'TotalExpected',
  'TotalCashInHand',
  'TotalIncome',
  'TotalVat',
  'TotalAdvance',
  'BalanceIncome',
  'TotalMoneyFromClient',
  'TotalClientMoney',
  'TotalDLV',
  'TotalRTN',
  'TotalCNL',
];

const FIRST_SUMMARY_COL_INDEX = PRINT_COLUMN_DEFS.findIndex((c) => SUMMARY_KEYS.includes(c.key));

function fieldFromRow(r, key) {
  const pascal = key;
  const camel = key.charAt(0).toLowerCase() + key.slice(1);
  return r[pascal] ?? r[camel];
}

function computeSums(rows) {
  const sums = Object.fromEntries(SUMMARY_KEYS.map((k) => [k, 0]));
  for (const r of rows) {
    for (const k of SUMMARY_KEYS) {
      sums[k] += parseNumberField(fieldFromRow(r, k));
    }
  }
  return sums;
}

function grandDlvPercent(sums) {
  const dlv = sums.TotalDLV;
  const rtn = sums.TotalRTN;
  const cnl = sums.TotalCNL;
  const den = dlv + rtn + cnl;
  if (den <= 0) return '—';
  return `${((dlv / den) * 100).toFixed(2)}%`;
}

function defaultPrintColumnSelection() {
  return Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, true]));
}

function filterSummary(f) {
  const parts = [`${f.dateFrom} → ${f.dateTo}`, 'shipment date'];
  if ((f.keyword || '').trim()) parts.push(`keyword "${(f.keyword || '').trim()}"`);
  if (f.employeeId) parts.push(`driver #${f.employeeId}`);
  const nos = shipmentNosFromStr(f.shipmentNosStr || '');
  if (nos.length) parts.push(`${nos.length} ref(s): ${nos.slice(0, 3).join(', ')}${nos.length > 3 ? '…' : ''}`);
  return parts.join(' · ');
}

function tdClass(key) {
  if (key === '#') return 'px-2 py-2 tabular-nums text-gray-600';
  if (RIGHT_ALIGN_KEYS.has(key)) return 'px-2 py-2 text-right tabular-nums';
  if (key === 'RefNo' || key === 'DeliveryNoteRef') return 'whitespace-nowrap px-2 py-2 font-mono text-[11px]';
  return 'whitespace-nowrap px-2 py-2';
}

export default function ShipmentReport() {
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const [draft, setDraft] = useState(() => defaultFilters());
  const [filterModalOpen, setFilterModalOpen] = useState(true);

  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [printPreparing, setPrintPreparing] = useState(false);
  const [printColumnModalOpen, setPrintColumnModalOpen] = useState(false);
  const [printColumnSelection, setPrintColumnSelection] = useState(defaultPrintColumnSelection);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await MainService.EmployeesInDropDown();
        if (!cancelled) setEmployees(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setEmployees([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (filterModalOpen) {
      setDraft({ ...appliedFilters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModalOpen]);

  const loadReport = async (f = appliedFilters) => {
    setLoading(true);
    setHasLoaded(false);
    setLoadProgress({ loaded: 0, total: 0 });
    setRows([]);
    setTotal(0);
    setError('');
    try {
      let skip = 0;
      let serverTotal = 0;
      const accumulated = [];
      for (;;) {
        const body = buildPayload(skip, FETCH_CHUNK, f);
        const res = await MainService.ShippingsInReportV2(body);
        const chunk = res?.Data ?? res?.data ?? [];
        const arr = Array.isArray(chunk) ? chunk : [];
        serverTotal = Number(res?.Total ?? res?.total ?? 0) || 0;
        accumulated.push(...arr);
        setRows([...accumulated]);
        setTotal(serverTotal);
        setLoadProgress({ loaded: accumulated.length, total: serverTotal });
        if (arr.length === 0) break;
        skip += arr.length;
        if (skip >= serverTotal) break;
        if (arr.length < FETCH_CHUNK) break;
      }
      setHasLoaded(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load shipment report.');
      setRows([]);
      setTotal(0);
      setHasLoaded(false);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  };

  const applyModalFilters = () => {
    setAppliedFilters({ ...draft });
    setFilterModalOpen(false);
    setTableSearch('');
    loadReport({ ...draft });
  };

  const cancelModal = () => {
    setDraft({ ...appliedFilters });
    setFilterModalOpen(false);
  };

  const openFilterModal = () => setFilterModalOpen(true);

  const onToolbarRefresh = () => {
    if (!hasLoaded || loading) return;
    setTableSearch('');
    loadReport(appliedFilters);
  };

  const clearModalFilters = () => {
    setDraft(defaultFilters());
  };

  const searchQ = tableSearch.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchQ) return rows;
    return rows.filter((r) => rowMatchesTableSearch(r, searchQ));
  }, [rows, searchQ]);

  const grandSums = useMemo(() => computeSums(filteredRows), [filteredRows]);

  const onExportExcel = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then export.');
      return;
    }
    setExcelExporting(true);
    setError('');
    try {
      const all = filteredRows;
      if (!all.length) {
        setError('No rows to export.');
        return;
      }
      const sheetData = all.map((r, i) => {
        const o = rowToFlat(r, i);
        const row = {};
        for (const d of PRINT_COLUMN_DEFS) {
          row[d.label] = o[d.key];
        }
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(sheetData);
      applyExcelHeaderRowStyle(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Shipments');
      const fname = `shipment-report-${appliedFilters.dateFrom}-${appliedFilters.dateTo}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Excel export failed.');
    } finally {
      setExcelExporting(false);
    }
  };

  const openPrintColumnModal = () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then print.');
      return;
    }
    if (!filteredRows.length) {
      setError('No rows to print.');
      return;
    }
    setError('');
    setPrintColumnSelection(defaultPrintColumnSelection());
    setPrintColumnModalOpen(true);
  };

  const executeShipmentPrint = async () => {
    const activeCols = PRINT_COLUMN_DEFS.filter((d) => printColumnSelection[d.key]);
    if (!activeCols.length) {
      setError('Select at least one column to print.');
      return;
    }

    setPrintColumnModalOpen(false);
    setPrintPreparing(true);
    setError('');
    try {
      const all = filteredRows;
      if (!all.length) {
        setError('No rows to print.');
        return;
      }

      const esc = (s) =>
        String(s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (e) {
        console.warn('Shipment report print: no print header', e);
      }

      const headHtml = `<tr>${activeCols.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr>`;
      const bodyHtml = all
        .map((r, i) => {
          const o = rowToFlat(r, i);
          const cells = activeCols.map((c) => o[c.key]);
          return `<tr>${cells.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
        })
        .join('');

      const summary = esc(filterSummary(appliedFilters));
      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header print-repeat-header-cell">${printHeaderHtml}</div>`
        : '';
      const thBg = REPORT_TABLE_HEADER_BG;
      const thColor = REPORT_TABLE_HEADER_TEXT;
      const thBorder = REPORT_TABLE_HEADER_BORDER;
      const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>&#x200b;</title>
        <style>
          html,body{margin:0;padding:0}
          body{font-family:system-ui,sans-serif;font-size:10px;color:#111}
          .report-print-inner{box-sizing:border-box;padding:12px}
          .company-print-header{margin-bottom:14px}
          .company-print-header img{max-height:72px}
          .report-title{font-size:13px;font-weight:700;margin:0 0 8px;color:#111}
          .meta{color:#444;margin-bottom:12px;max-width:100%}
          table{border-collapse:collapse;width:100%;table-layout:auto}
          th,td{border:1px solid ${thBorder};padding:3px 4px;word-wrap:break-word;vertical-align:top}
          th{background:${thBg} !important;color:${thColor};font-weight:700;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          td{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          @media print{
            @page{size:landscape;margin:0}
            html,body{margin:0 !important;padding:0 !important}
            .report-print-inner{padding:12mm 14mm 14mm 14mm !important;box-sizing:border-box;-webkit-box-decoration-break:clone;box-decoration-break:clone}
            thead{display:table-header-group}
            .company-print-header{page-break-inside:avoid}
          }
        </style></head><body>
        <div class="report-print-inner">
        ${headerBlock}
        <p class="report-title">Shipment report</p>
        <div class="meta">${summary}</div>
        <p style="margin:0 0 8px;font-size:10px;color:#666">${all.length} row(s)</p>
        <table><thead>${headHtml}</thead><tbody>${bodyHtml}</tbody></table>
        </div>
        </body></html>`;
      await printHtmlInSameTab(htmlDoc, 'Shipment report print');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Print failed.');
    } finally {
      setPrintPreparing(false);
    }
  };

  const updateDraft = useCallback((patch) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  return (
    <div className="space-y-4 relative">
      {printColumnModalOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shipment-report-print-columns-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <h3 id="shipment-report-print-columns-title" className="text-lg font-semibold text-gray-900">
                Columns for print / PDF
              </h3>
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-5 pt-3 text-sm text-gray-600">
              Choose which columns appear in the printable document. Company print header is added at the top automatically.
            </p>
            <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-gray-100">
              <button
                type="button"
                className="text-xs font-medium text-emerald-700 hover:underline"
                onClick={() => setPrintColumnSelection(defaultPrintColumnSelection())}
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() =>
                  setPrintColumnSelection(Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, false])))
                }
                className="text-xs font-medium text-gray-600 hover:underline"
              >
                Clear all
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-2">
              {PRINT_COLUMN_DEFS.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!printColumnSelection[col.key]}
                    onChange={() =>
                      setPrintColumnSelection((prev) => ({
                        ...prev,
                        [col.key]: !prev[col.key],
                      }))
                    }
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-800">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void executeShipmentPrint()}
                disabled={!PRINT_COLUMN_DEFS.some((d) => printColumnSelection[d.key])}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shipment-report-filter-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h3 id="shipment-report-filter-title" className="text-lg font-semibold text-gray-900">
                Shipment report filters
              </h3>
              <button
                type="button"
                onClick={cancelModal}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">From date</span>
                  <input
                    type="date"
                    value={draft.dateFrom}
                    onChange={(e) => updateDraft({ dateFrom: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">To date</span>
                  <input
                    type="date"
                    value={draft.dateTo}
                    onChange={(e) => updateDraft({ dateTo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Driver</span>
                  <select
                    value={draft.employeeId}
                    onChange={(e) => updateDraft({ employeeId: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                  >
                    <option value="">All drivers</option>
                    {employees.map((e) => (
                      <option key={e.Id ?? e.id} value={e.Id ?? e.id}>
                        {e.Name ?? e.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Keyword</span>
                  <input
                    type="text"
                    value={draft.keyword}
                    onChange={(e) => updateDraft({ keyword: e.target.value })}
                    placeholder="Shipment or delivery note ref…"
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="text-gray-600">Multiple shipment refs</span>
                  <input
                    type="text"
                    value={draft.shipmentNosStr}
                    onChange={(e) => updateDraft({ shipmentNosStr: e.target.value })}
                    placeholder="SHP001.SHP002.SHP003"
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm font-mono"
                  />

                </label>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={clearModalFilters}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={cancelModal}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyModalFilters}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              >
                Apply filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="page-title text-2xl font-semibold text-gray-900">Shipment Report</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={onToolbarRefresh}
            disabled={loading || !hasLoaded}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={excelExporting || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {excelExporting ? 'Exporting…' : 'Excel'}
          </button>
          <button
            type="button"
            onClick={openPrintColumnModal}
            disabled={printPreparing || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
            title="Choose columns, then print or Save as PDF (landscape suggested)"
          >
            <Printer className="w-4 h-4" />
            {printPreparing ? 'Preparing…' : 'Print / PDF'}
          </button>
        </div>
      </div>

      {hasLoaded && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Active filters: {filterSummary(appliedFilters)}
        </p>
      )}

      {(hasLoaded || loading) && (
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            disabled={loading && rows.length === 0}
            placeholder="Search loaded rows…"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-400"
            aria-label="Search in table"
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm">{error}</div>
      )}

      {!hasLoaded && !loading && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-gray-600 text-sm">
          Use <strong>Apply filters</strong> in the dialog to load the report (dialog opens automatically when you open this
          page).
        </div>
      )}

      <div className="flex max-h-[75vh] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading && loadProgress != null && (
          <div className="border-b border-gray-100 bg-white px-3 pb-2 pt-3">
            <div className="mb-1.5 flex justify-between text-xs text-gray-600">
              <span className="font-medium text-gray-800">Loading…</span>
              <span className="tabular-nums text-gray-700">
                {loadProgress.total > 0
                  ? `${loadProgress.loaded} / ${loadProgress.total} rows`
                  : rows.length > 0
                    ? `${rows.length} rows…`
                    : 'Connecting…'}
              </span>
            </div>
            {loadProgress.total > 0 ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.min(100, (loadProgress.loaded / Math.max(loadProgress.total, 1)) * 100)}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-emerald-400" />
              </div>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-[2800px] w-full text-left text-xs">
            <thead className="sticky top-0 z-10 border-b border-emerald-300 bg-emerald-100">
              <tr>
                {PRINT_COLUMN_DEFS.map((c) => (
                  <th key={c.key} className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading &&
                rows.length === 0 &&
                Array.from({ length: 8 }).map((_, si) => (
                  <tr key={`sk-${si}`} className="bg-white">
                    {Array.from({ length: COL_COUNT }).map((_, ci) => (
                      <td key={ci} className="px-2 py-2.5">
                        <div
                          className="h-3 rounded bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-pulse"
                          style={{ width: ci === 0 ? '1.5rem' : `${40 + ((si + ci * 3) % 5) * 12}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              {filteredRows.map((r, idx) => {
                const o = rowToFlat(r, idx);
                const rk = `${r.Id ?? r.id ?? 'row'}-${idx}`;
                return (
                  <tr key={rk} className="hover:bg-gray-50">
                    {PRINT_COLUMN_DEFS.map((c) => {
                      const raw = o[c.key];
                      const display =
                        c.key === 'DLVPer' || c.key === 'IsApproved' || c.key === 'Date' || !RIGHT_ALIGN_KEYS.has(c.key)
                          ? raw
                          : num(raw);
                      return (
                        <td key={c.key} className={tdClass(c.key)} title={String(raw ?? '')}>
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {hasLoaded && !loading && filteredRows.length > 0 && (
                <tr className="border-t-2 border-amber-400 bg-amber-100/95 text-amber-950 font-semibold">
                  <td
                    colSpan={FIRST_SUMMARY_COL_INDEX}
                    className="px-2 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-amber-900"
                  >
                    Grand total · {filteredRows.length} shipment{filteredRows.length === 1 ? '' : 's'}
                  </td>
                  {PRINT_COLUMN_DEFS.slice(FIRST_SUMMARY_COL_INDEX).map((c) => {
                    if (SUMMARY_KEYS.includes(c.key)) {
                      return (
                        <td key={c.key} className={tdClass(c.key)}>
                          {num(grandSums[c.key])}
                        </td>
                      );
                    }
                    if (c.key === 'DLVPer') {
                      return (
                        <td key={c.key} className={tdClass(c.key)}>
                          {grandDlvPercent(grandSums)}
                        </td>
                      );
                    }
                    return (
                      <td key={c.key} className="px-2 py-2 text-gray-400">
                        —
                      </td>
                    );
                  })}
                </tr>
              )}
              {loading &&
                rows.length > 0 &&
                Array.from({ length: 2 }).map((_, si) => (
                  <tr key={`skt-${si}`} className="bg-emerald-50/30">
                    {Array.from({ length: COL_COUNT }).map((_, ci) => (
                      <td key={ci} className="px-2 py-2">
                        <div className="h-2.5 w-[85%] rounded bg-gray-200 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              {!loading && hasLoaded && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-10 text-center text-gray-500">
                    {rows.length === 0 ? 'No rows for this filter.' : 'No rows match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span>
            {!hasLoaded && !loading && '—'}
            {loading &&
              `${rows.length} loaded${total ? ` of ${total}` : ''}${searchQ ? ` · ${filteredRows.length} match search` : ''}`}
            {hasLoaded &&
              !loading &&
              (searchQ
                ? `${filteredRows.length} rows (search) · ${rows.length} loaded · ${total} from server`
                : `${rows.length} rows (${total} from server)`)}
          </span>
        </div>
      </div>
    </div>
  );
}
